import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { FilterResultMethod } from '@toggle/toggle';

import { fetchWatchlistEntityQuery } from '~/hooks/use-watchlist-entity/useWatchlistEntity';
import { fetchKeyValues } from '~/modules/overview-key-values/services/entity-report-service';
import {
  EntityReturns,
  WatchlistEntities,
} from '~/stores/use-watchlist/watchlist-types';
import { LIVE_PRICE_COLUMN_META_VALUE_FIELD } from '~/views/screener/results/screener-table-utils';
import { fetchFilteredEntities } from '~/widgets/scenario/hooks/use-screener-entities-tags/entity-screener-service/entity-screener-service';
import { mapEntityTagsToDefaultOptions } from '~/widgets/scenario/hooks/use-screener-entities-tags/useScreenerEntitiesTags';

export type EntityPlusPrice = {
  prices?: WatchlistEntities['prices'];
  price: number;
  isLoading: WatchlistEntities['isLoading'];
  tag: WatchlistEntities['tag'];
  asset: WatchlistEntities['asset'];
  marketCap?: string;
} & Record<string, FilterResultMethod>;

const QUERY_KEY = 'watchlist-entities-table';

export const useAssetsTableData = (
  entities: WatchlistEntities[],
  pageSize = 15
) => {
  const mockLoadingState = Array.from({ length: 5 }, () => ({
    isLoading: true,
  })) as EntityPlusPrice[];

  return useInfiniteQuery<
    EntityPlusPrice[],
    Error,
    InfiniteData<EntityPlusPrice[]>,
    QueryKey,
    number
  >({
    queryKey: [QUERY_KEY, entities],
    queryFn: async ({ pageParam }) => {
      const start = pageParam * pageSize;
      const slice = entities.slice(start, start + pageSize).map(t => t.tag);
      const [settledResults, settledKeyValues, settledScreenerResults] =
        await Promise.all([
          Promise.allSettled(slice.map(tag => fetchWatchlistEntityQuery(tag))),
          Promise.allSettled(slice.map(tag => fetchKeyValues(tag))),
          Promise.allSettled(
            slice.map(tag =>
              fetchFilteredEntities(mapEntityTagsToDefaultOptions([tag]))
            )
          ),
        ]);

      const results = settledResults.map(r =>
        r.status === 'fulfilled' ? r.value : null
      );
      const keyValuesResults = settledKeyValues.map(r =>
        r.status === 'fulfilled' ? r.value : []
      );
      const entityScreenerResults = settledScreenerResults.map(r =>
        r.status === 'fulfilled' ? r.value : null
      );
      const filtered = results.filter(Boolean) as WatchlistEntities[];

      return filtered.map((data, idx) => {
        const keys = Object.keys(data.returns ?? []) as (keyof EntityReturns)[];
        const mappedReturns = keys.reduce((res, key) => {
          const entityReturn = (data.returns as EntityReturns)[key];
          res[key] = {
            value: entityReturn?.value,
            tooltip: entityReturn?.lastUpdate,
          };
          return res;
        }, {} as Record<string, FilterResultMethod>);
        const keyValues = keyValuesResults[idx];
        const marketCap = keyValues.find(
          item => item.key === 'market_cap'
        )?.value_fmt;

        const entityScreenerPrice =
          entityScreenerResults[idx]?.data?.[0]?.data?.[0]?.[
            LIVE_PRICE_COLUMN_META_VALUE_FIELD
          ]?.value;

        return {
          asset: data.asset,
          tag: data.tag,
          isLoading: false,
          prices: data.prices,
          price: entityScreenerPrice ?? data.prices?.last_value ?? 0,
          marketCap,
          ...mappedReturns,
        } as EntityPlusPrice;
      });
    },
    initialPageParam: 0,
    enabled: !!entities.length,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage.length === 0) {
        return null;
      }

      const lastEntity = lastPage.at(-1)?.tag;
      return lastEntity === entities.at(-1)?.tag ? null : lastPageParam + 1;
    },
    placeholderData: () => {
      return {
        pages: [mockLoadingState],
        pageParams: [0],
      };
    },
  });
};
